import { Box3, Group, Mesh, Object3D, Plane, Scene, Triangle, Vector3 } from "three";
import { getScaleWithMargin } from "./getScaleWithMargin";
import { useStoreRoom } from "../../../../store";
import {
    createAabbFootprint,
    createOrientedFootprintFromTemplate,
    createOrientedFootprintTemplate,
    intersectsOrientedFootprints,
    isBicycleDecor,
    OrientedFootprint,
} from "./orientedFootprint";
import { requiresPreciseWorkbenchToolboxBounds } from "../../../../utils/workbenchToolbox";

export type BBoxCollisionType = "aabb" | "triangular-prism" | "oriented-footprint";

export type BBoxItem = {
    aabb: Box3;
    center: Vector3;
    size: Vector3;
    collisionType?: BBoxCollisionType;
    triangles?: Triangle[];
    planes?: Plane[];
    footprint?: OrientedFootprint;
};

const PRECISE_COLLISION_EPSILON = 0.00001;

const getWorldTriangles = (object: Object3D): Triangle[] => {
    const triangles: Triangle[] = [];

    object.updateMatrixWorld(true);

    object.traverse((child) => {
        if (!(child instanceof Mesh)) return;

        const geometry = child.geometry;
        const position = geometry.getAttribute("position");
        if (!position) return;

        const index = geometry.getIndex();
        const triangleCount = Math.floor((index ? index.count : position.count) / 3);

        const getVertex = (vertexIndex: number) =>
            new Vector3(
                position.getX(vertexIndex),
                position.getY(vertexIndex),
                position.getZ(vertexIndex)
            ).applyMatrix4(child.matrixWorld);

        for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex++) {
            const offset = triangleIndex * 3;
            const indexA = index ? index.getX(offset) : offset;
            const indexB = index ? index.getX(offset + 1) : offset + 1;
            const indexC = index ? index.getX(offset + 2) : offset + 2;

            triangles.push(new Triangle(getVertex(indexA), getVertex(indexB), getVertex(indexC)));
        }
    });

    return triangles;
};

const getConvexPlanes = (triangles: Triangle[]): Plane[] => {
    if (!triangles.length) return [];

    const center = new Vector3();
    let vertexCount = 0;

    triangles.forEach((triangle) => {
        center.add(triangle.a);
        center.add(triangle.b);
        center.add(triangle.c);
        vertexCount += 3;
    });

    center.divideScalar(vertexCount);

    const planes: Plane[] = [];

    triangles.forEach((triangle) => {
        const edgeAB = triangle.b.clone().sub(triangle.a);
        const edgeAC = triangle.c.clone().sub(triangle.a);

        if (edgeAB.clone().cross(edgeAC).lengthSq() < Number.EPSILON) return;

        const plane = new Plane().setFromCoplanarPoints(triangle.a, triangle.b, triangle.c);

        if (plane.distanceToPoint(center) > 0) {
            plane.negate();
        }

        planes.push(plane);
    });

    return planes;
};

export const intersectsBBoxItem = (
    box: Box3,
    item: BBoxItem,
    currentFootprint?: OrientedFootprint
): boolean => {
    if (!box.intersectsBox(item.aabb)) return false;

    if (item.collisionType === "triangular-prism" && item.triangles?.length) {
        const preciseBox = box.clone();
        preciseBox.min.addScalar(PRECISE_COLLISION_EPSILON);
        preciseBox.max.addScalar(-PRECISE_COLLISION_EPSILON);

        if (preciseBox.isEmpty() || !preciseBox.intersectsBox(item.aabb)) {
            return false;
        }

        if (item.triangles.some((triangle) => preciseBox.intersectsTriangle(triangle))) {
            return true;
        }

        if (!item.planes?.length) return false;

        const boxCenter = preciseBox.getCenter(new Vector3());
        return item.planes.every((plane) => plane.distanceToPoint(boxCenter) <= PRECISE_COLLISION_EPSILON);
    }

    if (currentFootprint || item.collisionType === "oriented-footprint") {
        const firstFootprint = currentFootprint ?? createAabbFootprint(box);
        const secondFootprint = item.footprint ?? createAabbFootprint(item.aabb);

        return intersectsOrientedFootprints(firstFootprint, secondFootprint);
    }

    return true;
};

export const getDecorBboxCollection = (scene: Scene, currentObject: Group): BBoxItem[] => {
    const decors = useStoreRoom.getState().decors;
    const tireRacks = useStoreRoom.getState().tireRacks;
    const arr: BBoxItem[] = [];

    const currentObjectName = currentObject.name;

    scene.updateMatrixWorld(true);

    scene.traverse((child) => {
        const isDecor = child.name.startsWith("decor-");
        const isTireRack = child.name.startsWith("tireRack-");

        if ((!isDecor && !isTireRack) || child.name === currentObjectName) return;

        if (isDecor) {
            const id = child.name.replace("decor-", "");
            const decor = decors.find((item) => item.id === id);
            const scaledElem = child.clone();

            if (decor) {
                const scale = getScaleWithMargin(scaledElem, decor);
                scaledElem.scale.set(...scale);
            }

            scaledElem.updateMatrixWorld(true);

            const aabb = new Box3().setFromObject(
                scaledElem,
                decor ? requiresPreciseWorkbenchToolboxBounds(decor) : false
            );
            const center = aabb.getCenter(new Vector3());
            const size = aabb.getSize(new Vector3());

            const isTriangularCeilingConstruction =
                decor?.type === "constructions" &&
                decor.surface === "ceiling" &&
                decor.triangularPrism?.isTriangular === true;

            if (isTriangularCeilingConstruction) {
                const triangles = getWorldTriangles(scaledElem);

                if (triangles.length) {
                    arr.push({
                        aabb,
                        center,
                        size,
                        collisionType: "triangular-prism",
                        triangles,
                        planes: getConvexPlanes(triangles),
                    });
                    return;
                }
            }

            const usesOrientedFootprint = !!decor && isBicycleDecor(decor);

            if (usesOrientedFootprint && decor) {
                const footprintTemplate = createOrientedFootprintTemplate(child, decor.margin);
                const worldPosition = child.getWorldPosition(new Vector3());

                arr.push({
                    aabb,
                    center,
                    size,
                    collisionType: "oriented-footprint",
                    footprint: createOrientedFootprintFromTemplate(
                        footprintTemplate,
                        worldPosition,
                        decor.rotation.y
                    ),
                });
                return;
            }

            arr.push({ aabb, center, size, collisionType: "aabb" });
            return;
        }

        const scaledElem = child.clone();
        scaledElem.updateMatrixWorld(true);

        const aabb = new Box3().setFromObject(scaledElem);
        const center = aabb.getCenter(new Vector3());
        const size = aabb.getSize(new Vector3());

        arr.push({ aabb, center, size, collisionType: "aabb" });
    });

    return arr;
};
